import { isNumeric } from '../../util/helpers';

import { LatLon } from '../../util/geodesy/latlon';
import { parseLatLon } from '../../airport/util/parseUtils';
import { AviationEventInteractions } from './aviation-event-interactions.model';
import { AviationEventPhoto  } from './aviation-event-photo.model';
import { NgxGalleryImage } from 'ngx-gallery';


export class AviationEvent {
  eventId: number;

  title: string;

  description: string;

  shortDescription: string;

  isVisible: boolean;

  categoryId: number;

  type: AviationEventType;

  typeId: number;

  startDateTime: Date;

  endDateTime: Date;

  startDateTimeUTC: string;

  endDateTimeUTC: string;

  timezone: string;

  isAllDay: boolean;

  venueLocationName: string;

  address1: string;

  address2: string;

  address3: string;

  city: string;

  stateProvince: string;

  zipcode: string;

  country: string;

  countryCode: string;

  internationalAirportId: string;

  internationalAirportName: string;

  location: LatLon;

  latitude: number;

  longitude: number;

  contactPersonName: string;

  contactPersonEmail: string;

  contactPersonPhone: string;

  isFeatured: boolean;

  isValidated: boolean;

  airportId: number;

  airportIdent: string;

  hasAopaPresence: boolean;

  isAopaHosted: boolean;

  informationWebsite: string;

  websiteCustomLabel: string;

  registrationWebsite: string;

  cost: string;

  conferencePacketTitle: string;

  conferencePacketDescription: string;

  hostName: string;

  instructorName: string;

  webinarRegistrationLink: string;

  webinarRecording: string;

  recid: number;

  format: string;

  airportName: string;

  airportAddress1: string;

  airportAddress2: string;

  airportCity: string;

  airportState: string;

  airportZipcode: string;

  airportFuelTypes: string[];

  airportLongestRunway: string;

  interactions: AviationEventInteractions;

  photos: Array<AviationEventPhoto>;

  firstName: string;

  lastName: string;

  galleryPhotos: Array<NgxGalleryImage>;

  /**
   * Distance of airport from center of search area
   */
  distance: number;

  static parse(data: any): AviationEvent {
    return Object.assign(new AviationEvent(), {
      eventId: data.eventId,
      type: AviationEvent.parseEventType(data.typeId),
      typeId: data.typeId,
      title: data.title,
      description: data.description,
      shortDescription: data.shortDescription,
      isVisible: data.isVisible || true,
      categoryId: data.categoryId || null,
      startDateTime: data.startDateTimeUTC,
      endDateTime: data.endDateTimeUTC,
      startDateTimeUTC: data.startDateTimeUTC,
      endDateTimeUTC: data.endDateTimeUTC,
      timezone: data.timeZone,
      isAllDay: data.isAllDay || false,
      venueLocationName: data.venueLocationName || null,
      address1: data.address1 || null,
      address2: data.address2 || null,
      address3: data.address3 || null,
      city: data.city || null,
      stateProvince: data.stateProvince || null,
      zipcode: data.zipcode || null,
      country: data.country || null,
      countryCode: data.countryCode || null,
      internationalAirportId: data.internationalAirportId || null,
      internationalAirportName: data.internationalAirportName || null,
      location: data.latitude && data.longitude ? parseLatLon(data.latitude, data.longitude) : null,
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      contactPersonName: data.contactPersonName || null,
      contactPersonEmail: data.contactPersonEmail || null,
      contactPersonPhone: data.contactPersonPhone || null,
      isFeatured: data.isFeatured || false,
      isValidated: data.isValidated || false,
      airportId: data.airportId || null,
      airportIdent: data.airportIdent || null,
      hasAopaPresence: data.hasAopaPresence || false,
      isAopaHosted: data.isAopaHosted || false,
      informationWebsite: data.informationWebsite || null,
      websiteCustomLabel: data.websiteCustomLabel || null,
      registrationWebsite: data.registrationWebsite || null,
      cost: data.cost || null,
      conferencePacketTitle: data.conferencePacketTitle || null,
      conferencePacketDescription: data.conferencePacketDescription || null,
      hostName: data.hostName || null,
      instructorName: data.instructorName || null,
      webinarRegistrationLink: data.webinarRegistrationLink || null,
      webinarRecording: data.webinarRecording || null,
      distance: isNumeric(data.distance) ? +data.distance : null,
      recid: data.recId || null,
      format: data.format || null,
      airportName: data.airportName || null,
      airportAddress1: data.airportAddress1 || null,
      airportAddress2: data.airportAddress2 || null,
      airportCity: data.airportCity || null,
      airportState: data.airportState || null,
      airportZipcode: data.airportZipcode || null,
      airportFuelTypes: (data.airportFuelTypes || []).map(d => d),
      airportLongestRunway: data.airportLongestRunway || null,
      interactions: data.interactions ? AviationEventInteractions.parse(data.interactions) : null,
      photos: (data.photos || []).map(d => AviationEventPhoto.parse(d)),
      firstName: data.firstName || null,
      lastName: data.lastName || null,
      galleryPhotos: (data.photos || []).map(d => {
        return {
          big: d.largePhotoPath,
          medium: d.previewPhotoPath,
          small: d.thumbnailPhotoPath
        };
      })
    });
  }

  static parseEventType(typeId: number): AviationEventType {
    switch (typeId) {
      case 1:
        return AviationEventType.Airshow;
      case 2:
        return AviationEventType.FlyIn;
      case 3:
        return AviationEventType.FlyOut;
      case 4:
        return AviationEventType.PancakeBreakfast;
      case 5:
        return AviationEventType.SocialGathering;
      case 6:
        return AviationEventType.Openhouse;
      case 7:
        return AviationEventType.PokerRun;
      case 8:
        return AviationEventType.AviationCommunityDay;
      case 9:
        return AviationEventType.WingsAndWheelsEvent;
      case 10:
        return AviationEventType.AircraftDisplayRides;
      case 11:
        return AviationEventType.RustyPilotsSeminar;
      case 12:
        return AviationEventType.FlyingClubSeminar;
      case 13:
        return AviationEventType.SafetySeminar;
      case 14:
        return AviationEventType.Conference;
      case 15:
        return AviationEventType.Workshop;
      case 16:
        return AviationEventType.BackToYourRoots;
      case 17:
        return AviationEventType.AirshowwithMilitaryDemonstrationTeam;
      default:
        return null;
    }
  }

  static parseEventFromString(aviationEvent: string): AviationEventType {
    switch (aviationEvent) {
      case 'Airshow':
        return AviationEventType.Airshow;
      case 'Fly-In':
        return AviationEventType.FlyIn;
      case 'Fly-Out':
        return AviationEventType.FlyOut;
      case 'Pancake Breakfast':
        return AviationEventType.PancakeBreakfast;
      case 'Social Gathering':
        return AviationEventType.SocialGathering;
      case 'Openhouse':
        return AviationEventType.Openhouse;
      case 'Poker Run':
        return AviationEventType.PokerRun;
      case 'Aviation Community Day':
        return AviationEventType.AviationCommunityDay;
      case 'Wings and Wheels Event':
        return AviationEventType.WingsAndWheelsEvent;
      case 'Aircraft Displays/Rides':
        return AviationEventType.AircraftDisplayRides;
      case 'AOPA Rusty Pilots':
        return AviationEventType.RustyPilotsSeminar;
      case 'AOPA Flying Club':
        return AviationEventType.FlyingClubSeminar;
      case 'Safety Seminar':
        return AviationEventType.SafetySeminar;
      case 'Conference':
        return AviationEventType.Conference;
      case 'Workshop':
        return AviationEventType.Workshop;
      case 'AOPA Back to Your Roots':
        return AviationEventType.BackToYourRoots;
      case 'Airshow with Military Demonstration Team':
        return AviationEventType.AirshowwithMilitaryDemonstrationTeam;
      default:
        return null;
    }
  }
}

export enum AviationEventType {
  Airshow = 1,
  FlyIn = 2,
  FlyOut = 3,
  PancakeBreakfast = 4,
  SocialGathering = 5,
  Openhouse = 6,
  PokerRun = 7,
  AviationCommunityDay = 8,
  WingsAndWheelsEvent = 9,
  AircraftDisplayRides = 10,
  RustyPilotsSeminar = 11,
  FlyingClubSeminar = 12,
  SafetySeminar = 13,
  Conference = 14,
  Workshop = 15,
  BackToYourRoots = 16,
  AirshowwithMilitaryDemonstrationTeam = 17
}

export enum AviationEventTypeDesc {
    Airshow = 'Airshow',
    AirshowwithMilitaryDemonstrationTeam = 'Airshow with Military Demonstration Team',
    FlyIn = 'Fly-In',
    FlyOut = 'Fly-Out',
    PancakeBreakfast = 'Pancake Breakfast',
    SocialGathering = 'Social Gathering',
    Openhouse = 'Openhouse',
    PokerRun = 'Poker Run',
    AviationCommunityDay = 'Aviation Community Day',
    WingsAndWheelsEvent = 'Wings and Wheels Event',
    AircraftDisplayRides = 'Aircraft Displays/Rides',
    RustyPilotsSeminar = 'AOPA Rusty Pilots',
    FlyingClubSeminar = 'AOPA Flying Club',
    SafetySeminar = 'Safety Seminar',
    Conference = 'Conference',
    Workshop = 'Workshop',
    BackToYourRoots = 'AOPA Back to Your Roots'
}

export enum  TimeZones {
  ATLANTIC = 'America/Halifax',
  EASTERN = 'America/New_York',
  CENTRAL = 'America/Chicago',
  MOUNTAIN = 'America/Denver',
  PACIFIC = 'America/Los_Angeles',
  HAWAII = 'Pacific/Honolulu',
  ALASKA = 'America/Anchorage'
}
